// =====================================================================
// ICDocPnlSection.jsx
// =====================================================================
// Monthly P&L + EBITDA/ROI strip + Break-even rent section.
// Extracted from ICDocument.jsx (iter-35e split) — this was the largest
// single block of the printable IC document.
// =====================================================================

import { fmtINR, fmtL, fmtPct, decisionTone } from "./utils";
import { SectionTitle } from "./_ic_doc_primitives";
import { rentAssumption } from "./ICPnlPanel";

export default function ICDocPnlSection({ sc, rs, breakeven, decision, model }) {
  const cb = sc.cost_breakdown || {};
  const cbEntries = Object.entries(cb);
  const decisionLabel = decision || sc.decision_recommendation || "—";

  return (
    <>
      <section className="px-8">
        <SectionTitle accent="#9370DB">Monthly P&amp;L</SectionTitle>
        <table className="w-full text-[11.5px] border-collapse">
          <tbody>
            <PnlRevenueRows sc={sc} rs={rs} />
            <PnlExpenseRows cb={cb} cbEntries={cbEntries} sc={sc} />
            <PnlHostelProfitRow sc={sc} />
            <PnlCentralCostRow sc={sc} rs={rs} />
          </tbody>
        </table>

        <PnlEbitdaRoiStrip sc={sc} decisionLabel={decisionLabel} />
      </section>

      {breakeven && <BreakevenRentBanner title="Break-even rent · 36-month ROI" tag="Max" data={breakeven} model={model} current={breakeven} />}
      {breakeven?.min_rent && <BreakevenRentBanner title="Min rent · 36-month ROI" tag="Min" data={breakeven.min_rent} model={model} />}
    </>
  );
}

function PnlRevenueRows({ sc, rs }) {
  return (
    <>
      <tr className="border-b border-zinc-100">
        <td className="py-1.5 text-zinc-700">
          Dorm income <span className="text-zinc-400">({rs.sellable_dorm_beds ?? rs.dorm_beds ?? 0} beds · {fmtPct(sc.dorm_occ_pct)} · ₹{Math.round(sc.dorm_adr || 0)})</span>
        </td>
        <td className="py-1.5 text-right tabular-nums">{fmtINR(sc.dorm_revenue)}</td>
      </tr>
      <tr className="border-b border-zinc-100">
        <td className="py-1.5 text-zinc-700">
          Private income <span className="text-zinc-400">({rs.sellable_private_rooms ?? rs.private_rooms ?? 0} rooms · {fmtPct(sc.private_occ_pct)} · ₹{Math.round(sc.private_adr || 0)})</span>
        </td>
        <td className="py-1.5 text-right tabular-nums">{fmtINR(sc.private_revenue)}</td>
      </tr>
      <tr className="border-b border-zinc-100">
        <td className="py-1.5 text-zinc-700">F&amp;B revenue</td>
        <td className="py-1.5 text-right tabular-nums">{fmtINR(sc.fnb_revenue)}</td>
      </tr>
      <tr className="bg-emerald-50">
        <td className="py-2 font-display font-bold text-emerald-900">Total Revenue</td>
        <td className="py-2 text-right tabular-nums font-display font-bold text-emerald-900">{fmtINR(sc.total_revenue)}</td>
      </tr>
    </>
  );
}

function PnlExpenseRows({ cbEntries, sc }) {
  const visible = cbEntries.slice(0, 8);
  const overflow = cbEntries.slice(8);
  const overflowSum = overflow.reduce((a, [, v]) => a + (Number(v) || 0), 0);
  return (
    <>
      {visible.map(([li, val]) => (
        <tr key={li} className="border-b border-zinc-100">
          <td className="py-1.5 text-zinc-600 pl-3">{li}</td>
          <td className="py-1.5 text-right tabular-nums text-zinc-700">{fmtINR(val)}</td>
        </tr>
      ))}
      {overflow.length > 0 && (
        <tr className="border-b border-zinc-100">
          <td className="py-1.5 text-zinc-500 italic pl-3">+ {overflow.length} more line items</td>
          <td className="py-1.5 text-right tabular-nums text-zinc-500 italic">{fmtINR(overflowSum)}</td>
        </tr>
      )}
      <tr className="bg-rose-50">
        <td className="py-2 font-display font-bold text-rose-900">Total Operational Expense</td>
        <td className="py-2 text-right tabular-nums font-display font-bold text-rose-900">{fmtINR(sc.operational_cost)}</td>
      </tr>
    </>
  );
}

function PnlHostelProfitRow({ sc }) {
  return (
    <tr className="bg-emerald-50/80">
      <td className="py-2 font-display font-bold text-emerald-900">
        Hostel-Level Profit
        <span className="font-normal text-emerald-700 text-[10.5px] ml-2">
          (Margin {fmtPct(sc.hostel_level_profit_pct)})
        </span>
      </td>
      <td className="py-2 text-right tabular-nums font-display font-bold text-emerald-900">{fmtINR(sc.hostel_level_profit)}</td>
    </tr>
  );
}

function PnlCentralCostRow({ sc, rs }) {
  return (
    <tr className="border-b border-zinc-100">
      <td className="py-1.5 text-amber-800">
        Central cost <span className="text-zinc-400">(₹50/bed/day × {rs.total_beds || 0} × 30)</span>
      </td>
      <td className="py-1.5 text-right tabular-nums text-amber-800">{fmtINR(sc.central_cost)}</td>
    </tr>
  );
}

function PnlEbitdaRoiStrip({ sc, decisionLabel }) {
  return (
    <div className="grid grid-cols-3 gap-2 mt-3">
      <div className="rounded-lg p-3" style={{ background: "#09090B", color: "white" }}>
        <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-400">EBITDA / month</div>
        <div className="font-display font-black text-[22px] tabular-nums mt-1"
             style={{ lineHeight: 1.5, paddingBottom: 4, minHeight: 36 }}>{fmtINR(sc.ebitda)}</div>
        <div className="text-[10px] text-emerald-300 font-bold mt-0.5">{fmtPct(sc.ebitda_margin_pct)} margin</div>
      </div>
      <div className="rounded-lg p-3 bg-emerald-50 border border-emerald-200">
        <div className="text-[9px] font-bold uppercase tracking-wider text-emerald-700">Annual EBITDA</div>
        <div className="font-display font-black text-[22px] tabular-nums mt-1 text-emerald-900"
             style={{ lineHeight: 1.5, paddingBottom: 4, minHeight: 36 }}>{fmtL(sc.yearly_inflow)}</div>
        <div className="text-[10px] text-emerald-700 mt-0.5">Capex: {fmtL(sc.capex)}</div>
      </div>
      <div className={`rounded-lg p-3 border-2 ${decisionTone(decisionLabel)}`}>
        <div className="text-[9px] font-bold uppercase tracking-wider opacity-80">ROI / Decision</div>
        <div className="font-display font-black text-[22px] tabular-nums mt-1"
             style={{ lineHeight: 1.5, paddingBottom: 4, minHeight: 36 }}>{sc.roi_months ? `${sc.roi_months} mo` : "—"}</div>
        <div className="text-[11px] font-bold mt-0.5">{decisionLabel}</div>
      </div>
    </div>
  );
}

function BreakevenRentBanner({ title, tag, data, model, current }) {
  const feasible = data.feasible;
  const headerCls = feasible ? "text-indigo-700" : "text-rose-700";
  const subCls = feasible ? "text-indigo-600" : "text-rose-600";
  return (
    <section className="px-8 mt-4">
      <div className={`rounded-lg p-3 border-2 flex items-center justify-between gap-3 ${
        feasible ? "bg-indigo-50 border-indigo-300" : "bg-rose-50 border-rose-300"
      }`}>
        <div>
          <div className={`text-[10px] font-bold uppercase tracking-wider ${headerCls}`}>{title}</div>
          <div className={`text-[11px] mt-0.5 ${subCls}`}>{rentAssumption(tag, data, model)}</div>
          {current && (
            <div className={`text-[11px] mt-0.5 ${subCls}`}>
              Current rent: {fmtINR(current.current_rent_monthly)} · ₹{current.current_rent_per_bed_per_day}/bed/day
            </div>
          )}
        </div>
        {feasible ? <BreakevenFeasible breakeven={data} /> : <BreakevenInfeasible />}
      </div>
    </section>
  );
}

function BreakevenFeasible({ breakeven }) {
  return (
    <div className="text-right">
      <div className="font-display font-black text-[18px] text-indigo-900 tabular-nums"
           style={{ lineHeight: 1.35, paddingBottom: 2 }}>
        {fmtINR(breakeven.suggested_rent_monthly)}/mo
      </div>
      <div className="text-[10.5px] font-bold text-indigo-800">
        ₹{breakeven.suggested_rent_per_bed_per_day}/bed/day · ₹{breakeven.annual_rent_lakhs}L/yr
      </div>
    </div>
  );
}

function BreakevenInfeasible() {
  return (
    <div className="text-right">
      <div className="font-display font-black text-[14px] text-rose-900"
           style={{ lineHeight: 1.35, paddingBottom: 2 }}>Not feasible at current revenue</div>
      <div className="text-[10.5px] text-rose-700">Increase ADR / Occ to unlock 36-mo ROI</div>
    </div>
  );
}
